import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { FREE_MONTHLY_CALCS } from '../../pages/developers/snippets';
import { currentPlan, isPaidPlan } from '../../services/billingService';
import './app.css';
import '../stageshow/stageshow.css';

/**
 * Shared pieces for the signed-in screens (Hallmark app scope, Furrow in brand blue).
 * They only arrange content; every page keeps its own data calls and handlers.
 */

type Crumb = { label: string; to?: string };

export interface AppPageHeaderProps {
    title: React.ReactNode;
    description?: React.ReactNode;
    /** The page's one primary action, plus any quiet secondary actions, rendered right of the title. */
    actions?: React.ReactNode;
    /** A short line under the description: the company, the period, when it was updated. */
    meta?: React.ReactNode;
    crumbs?: Crumb[];
    id?: string;
}

export const AppPageHeader: React.FC<AppPageHeaderProps> = ({ title, description, actions, meta, crumbs, id }) => (
    <header className="gt-head" id={id}>
        <div className="gt-head-text">
            {crumbs && crumbs.length > 0 && (
                <nav aria-label="Breadcrumb" className="gt-crumbs">
                    <ol>
                        {crumbs.map((c, i) => (
                            <li key={`${c.label}-${i}`}>
                                {c.to ? <RouterLink to={c.to}>{c.label}</RouterLink> : <span aria-current="page">{c.label}</span>}
                            </li>
                        ))}
                    </ol>
                </nav>
            )}
            <Typography variant="h4" component="h1" className="gt-title">
                {title}
            </Typography>
            {description && (
                <Typography variant="body1" color="text.secondary" className="gt-desc">
                    {description}
                </Typography>
            )}
            {meta && (
                <Typography variant="body2" color="text.secondary" component="div" className="gt-meta">
                    {meta}
                </Typography>
            )}
        </div>
        {actions && <div className="gt-head-actions">{actions}</div>}
    </header>
);

export interface AppPageProps extends AppPageHeaderProps {
    children: React.ReactNode;
    /** 'wide' for tables and maps that want the full container, 'narrow' for forms. */
    width?: 'default' | 'wide' | 'narrow';
    hideHeader?: boolean;
}

/** One container recipe for every signed-in screen: capped, centred, the header on top. */
export const AppPage: React.FC<AppPageProps> = ({ children, width = 'default', hideHeader, ...header }) => (
    <Box className={`gt-page gt-page-${width}`}>
        {!hideHeader && <AppPageHeader {...header} />}
        {children}
    </Box>
);

export interface EmptyStateProps {
    title: string;
    body?: React.ReactNode;
    action?: { label: string; to?: string; href?: string; onClick?: () => void };
    secondary?: { label: string; to?: string; href?: string; onClick?: () => void };
    icon?: React.ReactNode;
}

const ActionButton: React.FC<{ a: NonNullable<EmptyStateProps['action']>; variant: 'contained' | 'outlined' | 'text' }> = ({ a, variant }) => {
    if (a.to) return <Button variant={variant} component={RouterLink} to={a.to} onClick={a.onClick}>{a.label}</Button>;
    if (a.href) return <Button variant={variant} href={a.href} onClick={a.onClick}>{a.label}</Button>;
    return <Button variant={variant} onClick={a.onClick} type="button">{a.label}</Button>;
};

/** What is empty, why it matters, the one action that fills it. */
export const EmptyState: React.FC<EmptyStateProps> = ({ title, body, action, secondary, icon }) => (
    <div className="gt-empty" role="status">
        <div className="gt-empty-still" aria-hidden="true">
            <div className="ss-chrome"><i /><i /><i /></div>
            {[72, 56, 64].map((w) => <div className="gt-empty-row" key={w}><b className="ss-bar" style={{ width: `${w}%` }} /><b className="ss-bar" style={{ width: 40 }} /></div>)}
        </div>
        {icon && <div className="gt-empty-icon" aria-hidden="true">{icon}</div>}
        <Typography variant="h6" component="h2">{title}</Typography>
        {body && <Typography variant="body2" color="text.secondary" className="gt-empty-body">{body}</Typography>}
        {(action || secondary) && (
            <div className="gt-empty-actions">
                {action && <ActionButton a={action} variant="contained" />}
                {secondary && <ActionButton a={secondary} variant="text" />}
            </div>
        )}
    </div>
);

/**
 * Plan prompt: the real tiers from the pricing section (Monitor free, Standard $49/month, Pro $149/month).
 * `feature` names what this screen does that the paid plans include.
 * Hidden for accounts already on Standard or Pro; shown if the plan cannot be read.
 */
export const PlanPrompt: React.FC<{ feature?: string; compact?: boolean }> = ({ feature, compact }) => {
    const [state, setState] = useState<'loading' | 'free' | 'paid'>('loading');
    useEffect(() => {
        let live = true;
        currentPlan()
            .then((plan) => { if (live) setState(isPaidPlan(plan) ? 'paid' : 'free'); })
            .catch(() => { if (live) setState('free'); });
        return () => { live = false; };
    }, []);
    if (state !== 'free') return null;
    return (
    <aside className={`gt-plan${compact ? ' gt-plan-compact' : ''}`} aria-label="Plans">
        <div className="gt-plan-text">
            <Typography variant="subtitle1" component="p" className="gt-plan-title">
                {feature ? `${feature} is part of Standard and Pro.` : 'Monitoring is free. Automation is on Standard and Pro.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="p">
                Monitor is free. Standard is <span className="num">$49</span>/month with automated calculations and multi-state compliance; Pro is <span className="num">$149</span>/month.
            </Typography>
        </div>
        <div className="gt-plan-actions">
            <Button variant="outlined" component={RouterLink} to="/billing">Compare plans</Button>
            <Button variant="text" component={RouterLink} to="/contact">Talk to sales</Button>
        </div>
    </aside>
    );
};

/** The developer API path: keys, the free monthly calculations, MCP. */
export const DeveloperPrompt: React.FC<{ compact?: boolean }> = ({ compact }) => (
    <aside className={`gt-dev${compact ? ' gt-plan-compact' : ''}`} aria-label="Developer API">
        <div className="gt-plan-text">
            <Typography variant="subtitle1" component="p" className="gt-plan-title">
                Calculate tax from your own checkout or agent.
            </Typography>
            <Typography variant="body2" color="text.secondary" component="p">
                The REST API and the MCP server use the same engine. <span className="num">{FREE_MONTHLY_CALCS.toLocaleString('en-US')}</span> live calculations a month are free.
            </Typography>
        </div>
        <div className="gt-plan-actions">
            <Button variant="outlined" component={RouterLink} to="/developers/console">Get an API key</Button>
            <Button variant="text" component={RouterLink} to="/developers">Read the docs</Button>
        </div>
    </aside>
);

/** A row of figures parted by hairlines; each figure is the product's own number. */
export const FigureRow: React.FC<{ items: { label: string; value: React.ReactNode; note?: React.ReactNode; tone?: 'error' | 'warn' | 'ok' }[] }> = ({ items }) => (
    <dl className="gt-figures" style={{ ['--cols' as any]: items.length }}>
        {items.map((it) => (
            <div key={it.label} className={`gt-figure${it.tone ? ` gt-tone-${it.tone}` : ''}`}>
                <dt>{it.label}</dt>
                <dd className="num">{it.value}</dd>
                {it.note && <dd className="gt-figure-note">{it.note}</dd>}
            </div>
        ))}
    </dl>
);

/** A titled section inside a screen: a heading row with an optional action, then the content on the sheet. */
export const AppSection: React.FC<{ title: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; flat?: boolean; id?: string; description?: React.ReactNode }> = ({ title, action, children, flat, id, description }) => (
    <section className={`gt-section${flat ? ' gt-section-flat' : ''}`} id={id}>
        <div className="gt-section-head">
            <div>
                <Typography variant="h6" component="h2">{title}</Typography>
                {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
            </div>
            {action && <div className="gt-section-action">{action}</div>}
        </div>
        <div className="gt-section-body">{children}</div>
    </section>
);
